import React from 'react'
import { Assessment } from '../assessmentDashboard'
import { LocalTable } from '../../../components/table'
import { DateWrapper } from '../../../components/simple/date'
import { useRequest } from '../../../request'
import { FloatingLoader } from '../../../components/simple/loader'
import { ProgressBar } from '../../../components/simple/progressBar'
import { Widget } from '../../dashboard/widgets'
import { DonutChart, PieData } from '../../../components/charts/pieChart'

export const StatsTab: React.FC<{
  assessment: Assessment
}> = ({ assessment }) => {
  const { data: stats } = useRequest<{
    views: {
      studentID: number
      name: string
      views: number
      firstView?: string
      quickestView?: string
    }[]
    languages: { name: string; percent: string; total: number }[]
    engagement: {
      student: {
        percent: string
        views: number
        total: number
      }
      guardian: {
        percent: string
        views: number
        total: number
      }
      multilingual: {
        percent: string
        views: number
        total: number
      }
    }
    sendStats: PieData
  }>(
    '/frontend_api/getAssessmentStats',
    React.useMemo(
      () => ({
        assessmentID: assessment.id,
      }),
      [assessment.id]
    )
  )

  return (
    <div className="mt-2 flex min-w-[500px] flex-col gap-2 self-center">
      {stats === undefined ? (
        <FloatingLoader />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex gap-6">
            {stats.languages.length > 0 && (
              <Widget className="bg-white">
                <div className="font-bold text-black/50">Document Languages</div>
                <div className="mt-1 flex flex-col gap-1">
                  {stats.languages.map((lang) => (
                    <div className="flex items-center justify-between" key={lang.name}>
                      <div className="min-w-0 overflow-hidden text-ellipsis">{lang.name}</div>
                      <ProgressBar
                        className="ml-2 min-w-40 max-w-40"
                        color="bg-sky-400"
                        percentage={lang.percent}
                        tooltip={`${lang.total} documents were sent in ${lang.name}`}
                      />
                    </div>
                  ))}
                  {Array.from({ length: Math.max(0, 9 - stats.languages.length) }).map((_, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="my-1 h-4 flex-grow rounded bg-gray-50"></div>
                      <ProgressBar className="ml-2 max-w-40 !bg-gray-50" percentage={0} />
                    </div>
                  ))}
                </div>
              </Widget>
            )}
            <Widget className="bg-white">
              <div className="font-bold text-black/50">Send Stats (Per Student)</div>
              <DonutChart className="mt-16 max-w-[350px]" legend={{ side: 'left' }} data={stats.sendStats} />
            </Widget>
            <Widget className="bg-white">
              <div className="font-bold text-black/50">Document Views</div>
              {stats.engagement.student.total && (
                <>
                  <div className="mt-4 font-[500] text-black/50">Students</div>
                  <ProgressBar
                    className="mt-1"
                    color="bg-violet-700"
                    percentage={stats.engagement.student.percent}
                    tooltip={`${stats.engagement.student.views} students viewed this document`}
                  />
                </>
              )}
              {stats.engagement.guardian.total && (
                <>
                  <div className="mt-4 font-[500] text-black/50">Guardians</div>
                  <ProgressBar
                    className="mt-1"
                    color="bg-violet-700"
                    percentage={stats.engagement.guardian.percent}
                    tooltip={`${stats.engagement.guardian.views} guardians viewed this document`}
                  />{' '}
                </>
              )}
              {stats.engagement.multilingual.total && (
                <>
                  <div className="mt-4 font-[500] text-black/50">Multilingual</div>
                  <ProgressBar
                    className="mt-1"
                    color="bg-violet-700"
                    percentage={stats.engagement.multilingual.percent}
                    tooltip={`${stats.engagement.multilingual.views} multilingual recipients viewed this document`}
                  />{' '}
                </>
              )}
            </Widget>
          </div>
          <div className="flex flex-col text-nowrap rounded-xl bg-white px-6 pb-6 pt-5 shadow-lg">
            <LocalTable
              data={stats.views}
              columns={[
                { header: 'Student ID', accessorKey: 'studentID' },
                { header: 'Name', accessorKey: 'name' },
                { header: 'Views', accessorKey: 'views' },
                {
                  header: 'First View',
                  accessorKey: 'firstView',
                  cell: ({ row }) => row.original.firstView && <DateWrapper relative date={row.original.firstView} />,
                },
                {
                  header: 'Quickest View',
                  accessorKey: 'quickestView',
                  cell: ({ row }) => row.original.quickestView && <>{row.original.quickestView} since send</>,
                },
              ]}
              className="self-stretch"
              exportName={`${assessment.name} Stats`}
              rowName="Sends"
            />
          </div>
        </div>
      )}
    </div>
  )
}
